// DevHub: primary navigation model for the Root header.
// Extracted from Root.tsx (behaviour-preserving split): the primary IA sections,
// the Admin/Docs dropdown items, the view-toggle page pairs, and the visibility
// helper. Root.tsx and the header sub-components import these back.
import { ROUTES } from '../common/constants/routes';

// External (partner) users belong to this group; internal users don't. Used to
// reduce the visible nav set and to render the Partner tier badge.
export const EXTERNAL_GROUP = 'group:default/external';

// Primary navigation — the IA sections as plain links. Each navigates to its
// native ODX landing/hub page; deeper navigation happens via cards on the page.
export interface NavSection {
  /** i18n key into commonMessages ('common' namespace), resolved at render. */
  labelKey: string;
  path: string;
  /** Navigation target when clicked, if it differs from `path` (which is still used for
   *  active-highlight matching). Lets "Self Service" highlight across the whole hub while
   *  landing you on the Create catalog. */
  to?: string;
  /** Extra path prefixes that also count as "being in this section" for the active
   *  highlight — so every route under a section lights up its top-nav item (not just the
   *  section's own landing page). e.g. Catalog owns /catalog*, entity pages and /api-docs. */
  match?: string[];
  /** Path prefixes that must NOT highlight this section even if `match` catches them —
   *  used where sections overlap (Guides is a specific doc that lives under /docs). */
  exclude?: string[];
  /** Visible to external (partner) users. Internal users see all sections. */
  external?: boolean;
}

// A dropdown entry (Admin / Docs jump-off menus). labelKey resolves at render.
export interface NavItem {
  labelKey: string;
  path: string;
  icon: string;
}

export const GUIDES_PATH = '/docs/default/component/devhub-guides';

export const sections: NavSection[] = [
  // Getting Started — the Guides doc leads the IA. It lives under /docs but owns
  // its own top-nav item; Docs excludes this path so the two don't both highlight.
  { labelKey: 'nav.guides', path: GUIDES_PATH },
  // Catalog owns the whole catalog surface + entity pages + the API registry + the
  // MCP marketplace (an McpServer is a catalog kind, so the marketplace lives here).
  {
    labelKey: 'nav.catalog',
    path: ROUTES.overview.catalog,
    match: [ROUTES.catalog.table, ROUTES.api.docs, ROUTES.resources.mcpCatalog],
    external: true,
  },
  // Docs owns every /docs* route (readers, index, curated) EXCEPT the Guides doc.
  {
    labelKey: 'nav.docs',
    path: ROUTES.overview.docs,
    match: [ROUTES.docs.all],
    exclude: [GUIDES_PATH],
    external: true,
  },
  // Resources is the hub for the Landscape, System Status and Support. The Tech Radar
  // is off in this deployment (devhub.features.techRadar), so its path is not matched here —
  // nothing can be "on" a route that renders the 404. Add it back with the radar.
  {
    labelKey: 'nav.resources',
    path: ROUTES.overview.resources,
    match: [ROUTES.resources.landscape, ROUTES.systemStatus],
  },
  { labelKey: 'nav.news', path: ROUTES.announcements, external: true },
  { labelKey: 'nav.community', path: ROUTES.overview.community },
];

// Admin dropdown — management views for admins only. labelKey → commonMessages ('admin.*').
// Admin dropdown — consolidated to four entries. Analytics and Curation are tabbed
// pages (Impact/Usage/AI and Content/Radar/MCP respectively); Access holds role
// assignments + tier management. The DevSecOps hub is the main-nav "Self Service"
// entry, not an admin item.
// Overview removed — the four merged sections. /admin redirects to Insights.
export const adminItems: NavItem[] = [
  { labelKey: 'admin.insights', path: ROUTES.admin.insights, icon: 'core::dashboard' },
  { labelKey: 'admin.content', path: ROUTES.admin.curation, icon: 'core::config' },
  { labelKey: 'admin.access', path: ROUTES.admin.access, icon: 'core::group' },
  { labelKey: 'admin.system', path: ROUTES.admin.resources, icon: 'core::cloud-ok' },
];

// Docs dropdown — jump-off to the curated landing vs. the full TechDocs index.
// Routes are defined in App.tsx (/docs-curated, /docs).
export const docsItems: NavItem[] = [
  { labelKey: 'docsMenu.gettingStarted', path: GUIDES_PATH, icon: 'core::training' },
  { labelKey: 'docsMenu.curated', path: ROUTES.docs.curated, icon: 'core::bookmark' },
  { labelKey: 'docsMenu.all', path: ROUTES.docs.all, icon: 'core::document' },
];

// View toggle — portaled into ContentHeader's button area on stock pages,
// rendered inline on our custom card pages.
// Catalog is card-only now (its list/table toggle was cut), so it's no longer
// listed here. The table view still exists at /catalog?view=table but is unlinked.
export const viewTogglePages = [
  { tablePath: ROUTES.api.docs, cardPath: ROUTES.api.cards },
];

// Determine which primary nav items + catalog dropdown items are visible
export function getVisibleSections(groups: string[]): NavSection[] {
  const isExternal = groups.includes(EXTERNAL_GROUP);
  // External (registered) partners see a reduced set (the `external` sections);
  // internal users see every section.
  return isExternal ? sections.filter(s => s.external) : sections;
}
